import React, { useState } from 'react';
import { Field, Modal, Spinner } from '../../components/ui';
import ExportButton from '../../components/ExportButton';
import { EXTRA_PERSONS_PER_CARD, fmtDate, fmtDateTime } from '../../utils/helpers';
import { GUEST_TYPES } from './useCards';
import '../../styles/money.css';

// Desktop Cards — card distribution: whose round each card is on, whether it
// has been given and WHEN (both editable), what to ORDER per card type, and
// the printable sheet of names. Logic: useCards.
export default function CardsDesktop({ vm }) {
  const {
    canView, canEdit, canManageTypes, cards, counts, loading, functions, event, fnName,
    fnFilter, setFnFilter, typeFilter, setTypeFilter, cardTypeFilter, setCardTypeFilter,
    statusFilter, setStatusFilter, q, setQ,
    adderFilter, setAdderFilter, adderOptions, adder, adderSummary, addedByMe, myAdded,
    showAllAdders, showMyAdded, shapeFilter, setShapeFilter, shapeCounts,
    quickQ, setQuickQ, quickMatches, quickGive, clearQuick, giversReport,
    cardTypes, orderTotals, withoutCard, ownFamily, extraTotals, delivererNames,
    setExtraCards, setExtraPersons, setDeliverer,
    selected, toggleSelect, allVisibleSelected, toggleSelectAll, selectedCount, bulkDeliverer, bulkStatus,
    editing, setEditing, cardForm, setCardF, saveCard, saving, openCard,
    myName,
    markGiven, givenForm, openGiven, closeGiven, setGivenF, givenNow, saveGiven, unGive, bulkGiveNow, bulkTake,
    guests,
    typeForm, openTypeAdd, openTypeEdit, closeType, setTypeF, toggleTypeFunction, toggleTypeExtraFunction, saveType, deleteType, addDefaultTypes,
    CARD_KINDS, typeFnIds, print,
  } = vm;
  const [bulkName, setBulkName] = useState('');

  if (!canView) {
    return (
      <div>
        <div className="page-head"><h1>Cards</h1></div>
        <div className="card card-pad empty">You do not have access to the guest list of this event.</div>
      </div>
    );
  }
  // Commit a typed value on blur / Enter (inputs are uncontrolled, keyed by value).
  const commitOnEnter = (e) => { if (e.key === 'Enter') e.currentTarget.blur(); };
  // Enter in a text box of a dialog saves it.
  const enterTo = (fn) => (e) => {
    if (e.key !== 'Enter' || e.defaultPrevented || saving) return;
    const t = e.target;
    if (t.tagName !== 'INPUT' || t.type === 'checkbox' || t.getAttribute('list')) return;
    e.preventDefault();
    fn();
  };
  // A function as a compact bordered row (the CountGroupForm look).
  const fnRow = (f, on, onToggle) => (
    <label key={f.FunctionId} className={'cg-fn' + (on ? ' on' : '')}>
      <input type="checkbox" checked={on} onChange={onToggle} />
      <span className="cg-fn-name" title={f.FunctionName}>{f.FunctionName}</span>
      {f.FunctionDate && <span className="cg-fn-date">{fmtDate(f.FunctionDate).slice(0, 5)}</span>}
    </label>
  );

  return (
    <div className="cards-page">
      <div className="page-head no-print">
        <h1>Cards</h1>
        <span className="muted">
          <strong>{counts.given}</strong> given · <strong>{counts.pending}</strong> pending of {counts.cards} cards
          {ownFamily > 0 && <> · <span title="Own family and groups marked No card - counted at the functions, no card">{ownFamily} without a card</span></>}
        </span>
        <div className="spacer" />
        {guests?.canAdd && !!guests.invitableFunctions?.length && (
          <>
            <button className="btn light" onClick={() => guests.openAdd('individual')}>👤 Guest</button>
            <button className="btn light" onClick={() => guests.openAdd('family')}>👪 Family</button>
          </>
        )}
        <button className="btn light" onClick={print}>🖨 Print names</button>
      </div>

      {/* ── Whose cards, and how far they have got ────────────────── */}
      <div className="card card-pad mn-cd-round no-print">
        {/* Whoever invited a household gives it their card, so THIS is the
            first question; who is carrying the bundle is the one beside it. */}
        <label className="field mn-cd-who-field">Cards to give by
          <select value={adderFilter} onChange={e => setAdderFilter(e.target.value)}>
            <option value="">All members · {adderSummary.total} cards</option>
            {adderOptions.map(a => (
              <option key={a.value} value={a.value}>
                {a.label} · {a.total ? `${a.given}/${a.total} given` : 'no cards'}
              </option>
            ))}
          </select>
        </label>
        <div className="mn-cd-tiles">
          <div className="mn-cd-tile"><b>{adderSummary.total}</b><span>Total</span></div>
          <div className="mn-cd-tile is-given"><b>{adderSummary.given}</b><span>Given</span></div>
          <div className="mn-cd-tile is-pending"><b>{adderSummary.pending}</b><span>Pending</span></div>
        </div>
        <div className="mn-seg mn-cd-seg">
          {[['', 'All'], ['Pending', 'Pending'], ['Delivered', 'Given']].map(([v, label]) => (
            <button key={v} type="button" className={statusFilter === v ? 'on' : ''} onClick={() => setStatusFilter(v)}>
              {label} · {v === '' ? counts.cards : v === 'Pending' ? counts.pending : counts.given}
            </button>
          ))}
        </div>
        <div className="muted mn-cd-scope">
          {addedByMe ? <>Cards you invited · <button className="linklike" onClick={showAllAdders}>show everyone</button></>
            : adder ? (adder.noAdder
              ? <>Cards with <strong>no member recorded</strong> · <button className="linklike" onClick={showAllAdders}>show everyone</button></>
              : <>Invited by <strong>{adder.name}</strong> · <button className="linklike" onClick={showAllAdders}>show everyone</button></>)
              : (
                <>
                  Everyone's cards
                  {myAdded ? <> · <button className="linklike" onClick={showMyAdded}>only mine</button></>
                    : ' · you have invited nobody yet'}
                </>
              )}
          {/* A filter left on elsewhere would silently hide cards, so it is
              always said here, with the way out beside it. */}
          {shapeFilter && <> · {shapeFilter === 'family' ? 'families only' : 'single guests only'} <button className="linklike" onClick={() => setShapeFilter('')}>clear</button></>}
        </div>
      </div>

      {/* ── What to order ─────────────────────────────────────────── */}
      <div className="card card-pad cards-order no-print">
        <div className="cards-order-head">
          <strong>Cards to order</strong>
          {canManageTypes && <button className="btn light sm" onClick={openTypeAdd}>+ Card type</button>}
          {/* The distribution sheet: every member's cards / given / pending,
              for whoever is chosen in "Cards to give by". */}
          <ExportButton report={giversReport} label="⇪ PDF - cards to give" title="Members with cards, given and pending - PDF or Excel" />
        </div>
        {cardTypes.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            No card types yet. A card type says which functions a card is for, e.g. the <strong>Reception card</strong> for everyone
            and a <strong>Thread Ceremony insert</strong> (extra paper inside) for those invited to the thread ceremony.
            {canManageTypes && functions.length > 0 && (
              <div style={{ marginTop: 8 }}><button className="btn primary sm" onClick={addDefaultTypes}>Set up Reception card + Thread Ceremony insert</button></div>
            )}
          </div>
        ) : (
          <div className="cards-order-tiles">
            {orderTotals.map(({ type, households, extra, total }) => (
              <div key={type.CardTypeId} className={'cards-order-tile' + (String(cardTypeFilter) === String(type.CardTypeId) ? ' on' : '')}
                onClick={() => setCardTypeFilter(String(cardTypeFilter) === String(type.CardTypeId) ? '' : String(type.CardTypeId))}
                title="Show only the households that get this card">
                <div className="cards-order-total">{total}</div>
                <div className="cards-order-name">{type.CardTypeName} <span className="chip">{type.Kind}</span></div>
                <div className="muted">{households} households{extra ? ` + ${extra} extra cards` : ''}</div>
                {Number(type.ForExtraCards) === 1 && (
                  <div className="muted cards-order-fns">Extra-card guests: {String(type.ExtraFunctionIds || type.FunctionIds || '').split(',').map(fnName).filter(Boolean).join(', ')}</div>
                )}
                <div className="muted cards-order-fns">{typeFnIds(type).map(fnName).filter(Boolean).join(', ')}</div>
                {canManageTypes && (
                  <div className="cards-order-tools" onClick={e => e.stopPropagation()}>
                    <button className="icon-btn" title="Edit" onClick={() => openTypeEdit(type)}>✎</button>
                    <button className="icon-btn danger" title="Remove" onClick={() => deleteType(type)}>🗑</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Extra cards: <strong>{extraTotals.cards}</strong> → <strong>{extraTotals.persons}</strong> persons added to the Reception headcount
          ({EXTRA_PERSONS_PER_CARD} per card unless a number is typed).
          {withoutCard > 0 && <> · <span style={{ color: 'var(--danger)' }}>{withoutCard} households are in no card type</span></>}
        </div>
      </div>

      {/* ── Every member's share: cards, given, pending ────────────── */}
      {adderOptions.length > 1 && (
        <div className="card card-pad mn-cd-people no-print">
          <div className="cards-order-head">
            <strong>Cards by the member who invited them</strong>
            <span className="muted" style={{ fontSize: 12 }}>
              whole event, whatever the filters show{adder ? ` · showing ${adder.name}` : ''}
            </span>
            <div className="spacer" />
            <ExportButton report={giversReport} label="⇪ PDF" title="This table as a PDF or Excel file" />
          </div>
          <table className="tbl compact mn-cd-people-tbl">
            <thead>
              <tr>
                <th className="grow">Added by</th>
                <th className="num" title="Invitation cards to give - one per household">Cards</th>
                <th className="num" title="Handed to the guest">Given</th>
                <th className="num" title="Still to hand over">Pending</th>
                <th className="num" title="Households that get one card between them">Families</th>
              </tr>
            </thead>
            <tbody>
              {adderOptions.map(a => (
                <tr key={a.value} className={String(adderFilter) === String(a.value) ? 'selected' : undefined}
                  onClick={() => setAdderFilter(String(adderFilter) === String(a.value) ? '' : String(a.value))}
                  style={{ cursor: 'pointer' }} title="Show only this member's cards">
                  <td className="grow">{a.label}</td>
                  {/* One card per household. The extra copies a head asked
                      for are an ORDER figure, never part of what is handed
                      over, so they are noted quietly and never added in. */}
                  <td className="num"><strong>{a.total}</strong>{a.extraCards ? <span className="muted" title={`${a.extraCards} extra copies to order for these households`}> +{a.extraCards}</span> : null}</td>
                  <td className="num">{a.given}</td>
                  <td className="num">{a.pending || <span className="muted">—</span>}</td>
                  <td className="num muted">{a.families}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Search a name and give that card ──────────────────────── */}
      {canEdit && (
        <div className="card card-pad mn-cd-quick no-print">
          <label className="field mn-grow">Give a card — search the name
            <input type="search" value={quickQ} placeholder="two letters of the name, or a number"
              onChange={e => setQuickQ(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') { clearQuick(); return; }
                // Enter gives it only when the search has found exactly one
                // person - never a guess at which of several was meant.
                if (e.key === 'Enter' && quickMatches.length === 1 && quickMatches[0].status !== 'Delivered') quickGive(quickMatches[0]);
              }} />
          </label>
          {quickQ.trim().length >= 2 && (
            <div className="mn-cd-hits">
              {quickMatches.length === 0 ? <div className="muted">Nobody of that name has a card.</div>
                : quickMatches.map(c => (
                  <div key={c.key} className="mn-cd-hit">
                    <div className="mn-cd-hit-who">
                      <strong>{c.name}</strong>
                      <span className="muted">
                        {[c.mobile, c.city, c.persons > 1 ? `${c.persons} people` : 'on their own',
                          c.addedBy ? `added by ${c.addedBy}` : ''].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    {c.status === 'Delivered'
                      ? <span className="chip ok" title={`Given by ${c.deliveredBy || 'someone'}`}>✓ {fmtDateTime(c.deliveredDate)}</span>
                      : <button className="btn primary sm" onClick={() => quickGive(c)}>Give</button>}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* ── Filters ───────────────────────────────────────────────── */}
      <div className="cards-filters no-print">
        <select value={fnFilter} onChange={e => setFnFilter(e.target.value)} title="Filter by function">
          <option value="">All functions</option>
          {functions.map(f => <option key={f.FunctionId} value={f.FunctionId}>{f.FunctionName}</option>)}
        </select>
        <select value={cardTypeFilter} onChange={e => setCardTypeFilter(e.target.value)} title="Card type">
          <option value="">All card types</option>
          {cardTypes.map(t => <option key={t.CardTypeId} value={t.CardTypeId}>{t.CardTypeName}</option>)}
        </select>
        <select value={adderFilter} onChange={e => setAdderFilter(e.target.value)} title="Who invited these guests">
          <option value="">Added by anyone</option>
          {adderOptions.map(a => (
            <option key={a.value} value={a.value}>{a.label} · {a.total ? `${a.given}/${a.total} given` : 'no cards'}</option>
          ))}
        </select>
        <select value={shapeFilter} onChange={e => setShapeFilter(e.target.value)} title="A card for one person, or for a household">
          <option value="">Single + family · {shapeCounts.all}</option>
          <option value="single">Single guests · {shapeCounts.single}</option>
          <option value="family">Families · {shapeCounts.family}</option>
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} title="Family or business">
          <option value="">Family + business</option>
          {GUEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="search" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {canEdit && selectedCount > 0 && (
        <div className="cards-bulk no-print">
          <strong>{selectedCount} selected</strong>
          <input list="card-deliverers" placeholder="Hand to…" value={bulkName} onChange={e => setBulkName(e.target.value)} />
          <button className="btn light sm" title="These cards are now in that person's hand - not given to the guests yet"
            onClick={() => { bulkDeliverer(bulkName); setBulkName(''); }}>Hand over</button>
          {myName && <button className="btn light sm" title="Take these myself" onClick={bulkTake}>📥 Take</button>}
          <button className="btn primary sm" onClick={bulkGiveNow}>✓ Given by me now</button>
          <button className="btn light sm" onClick={() => bulkStatus('Pending')}>Back to pending</button>
        </div>
      )}
      <datalist id="card-deliverers">{delivererNames.map(n => <option key={n} value={n} />)}</datalist>

      {/* ── One row per card ──────────────────────────────────────── */}
      {loading ? <div className="card card-pad"><Spinner /></div>
        : cards.length === 0 ? <div className="card card-pad empty">No cards match.</div> : (
          <div className="card table-wrap no-print">
            <table className="tbl compact cards-table">
              <thead>
                <tr>
                  {canEdit && <th><input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} title="Select all shown" /></th>}
                  <th className="num">#</th>
                  <th className="grow">Name on card</th>
                  <th>Cards</th>
                  <th className="num" title="Extra cards this head asked for (names unknown, Reception only). Shown here, never added to the counts above - a household is ONE card to give.">Extra cards</th>
                  <th>Given by</th>
                  <th title="When the card was handed over - click to change">Given</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c, i) => (
                  <tr key={c.key} className={selected.has(c.key) ? 'selected' : undefined}>
                    {canEdit && <td><input type="checkbox" checked={selected.has(c.key)} onChange={() => toggleSelect(c.key)} /></td>}
                    <td className="num muted">{i + 1}</td>
                    <td className="grow">
                      <button className="linklike cards-name" disabled={!canEdit} onClick={() => openCard(c)} title="Edit the name on the card">{c.name}</button>
                      <div className="sub">
                        {[c.subtitle, c.mobile, c.city,
                          // Who invited them - said once in the header when one
                          // member is chosen, so only shown on the row otherwise.
                          !adder && c.addedBy ? `by ${c.addedBy}` : ''].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td>
                      {c.cardTypes.length
                        ? c.cardTypes.map(t => <span key={t.CardTypeId} className={'chip ' + (t.Kind === 'Insert' ? 'warn' : 'ok')} style={{ marginRight: 4 }}>{t.CardTypeName}</span>)
                        : <span className="muted">{cardTypes.length ? 'none' : '—'}</span>}
                    </td>
                    <td className="num">
                      <input key={`x${c.key}${c.extraCards}`} className="cards-num" type="number" min="0" max="99" defaultValue={c.extraCards || ''}
                        disabled={!canEdit} placeholder="0" onBlur={e => setExtraCards(c, e.target.value)} onKeyDown={commitOnEnter} />
                    </td>
                    <td>
                      <input key={`d${c.key}${c.deliveredBy}`} className="cards-deliverer" list="card-deliverers" defaultValue={c.deliveredBy}
                        disabled={!canEdit} placeholder="—" onBlur={e => setDeliverer(c, e.target.value)} onKeyDown={commitOnEnter} />
                    </td>
                    <td className="mn-cd-given-cell">
                      {c.status === 'Delivered' ? (
                        <button className="chip ok" disabled={!canEdit} title="Given — click to change who gave it, or when"
                          onClick={() => openGiven(c)}>✓ {fmtDateTime(c.deliveredDate)}</button>
                      ) : (
                        <>
                          <button className="btn primary sm" disabled={!canEdit} title="Given by me, now"
                            onClick={() => markGiven(c)}>Give</button>
                          <button className="icon-btn" disabled={!canEdit} title="Given by someone else, or at another time"
                            onClick={() => openGiven(c)}>🕑</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* ── Printable sheet of names (print only) ─────────────────── */}
      <div className="print-title" style={{ display: 'none' }}>{event?.EventName}</div>
      <div className="card-sheet cards-print-sheet">
        {cards.map((c, i) => (
          <div key={c.key} className="guest-card">
            <div className="guest-card-no">{i + 1}</div>
            <div className="guest-card-name">{c.name}</div>
            {c.subtitle && <div className="guest-card-sub">{c.subtitle}</div>}
            <div className="guest-card-meta">
              {c.mobile}{c.city ? ` · ${c.city}` : ''}{c.extraCards ? ` · +${c.extraCards} extra` : ''}{c.deliveredBy ? ` · by ${c.deliveredBy}` : ''}
            </div>
            {c.cardTypes.length > 0 && <div className="guest-card-fns">{c.cardTypes.map(t => t.CardTypeName).join(' + ')}</div>}
          </div>
        ))}
      </div>

      {givenForm && (
        <Modal className="mn-card-modal" title={`Given · ${givenForm.card.name}`} onClose={closeGiven}
          footer={<>
            {givenForm.card.status === 'Delivered' && (
              <button className="btn light" disabled={saving} onClick={unGive}>Not given yet</button>
            )}
            <div className="spacer" />
            <button className="btn light" onClick={closeGiven}>Cancel</button>
            <button className="btn primary" data-save="primary" disabled={saving} onClick={saveGiven}>
              {saving ? 'Saving…' : givenForm.card.status === 'Delivered' ? 'Save' : '✓ Given'}
            </button>
          </>}>
          <div onKeyDown={enterTo(saveGiven)}>
            <div className="mn-covers">
              {[givenForm.card.mobile, givenForm.card.city,
                givenForm.card.persons > 1 ? `${givenForm.card.persons} people` : '',
                givenForm.card.extraCards ? `+${givenForm.card.extraCards} extra cards` : ''].filter(Boolean).join(' · ')}
            </div>
            <div className="mn-line">
              <label className="field mn-grow">Given by
                <input autoFocus list="card-deliverers" value={givenForm.by ?? ''} placeholder="who handed it over"
                  onChange={e => setGivenF('by', e.target.value)} />
              </label>
              <label className="field">When
                <input type="datetime-local" value={givenForm.when ?? ''} onChange={e => setGivenF('when', e.target.value)} />
              </label>
              <button className="btn light sm" style={{ alignSelf: 'end' }} onClick={givenNow}>Now</button>
            </div>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal className="mn-card-modal" title={`Card · ${editing.name}`} onClose={() => setEditing(null)}
          footer={<>
            <span className="mn-sum">{editing.members.length} {editing.members.length === 1 ? 'person' : 'people'} on this card</span>
            <button className="btn light" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn primary" disabled={saving} onClick={saveCard}>{saving ? 'Saving…' : 'Save'}</button>
          </>}>
          <div onKeyDown={enterTo(saveCard)}>
            <div className="mn-line">
              <div className="mn-seg-cell">
                <span className="mn-seg-label">Card for</span>
                <div className="mn-seg">
                  {GUEST_TYPES.map(t => (
                    <button key={t} type="button" className={cardForm.GuestType === t ? 'on' : ''} onClick={() => setCardF('GuestType', t)}>{t}</button>
                  ))}
                </div>
              </div>
              {cardForm.GuestType === 'Business' && (
                <div className="mn-grow">
                  <Field label="Company name" value={cardForm.CompanyName} onChange={(v) => setCardF('CompanyName', v)} />
                </div>
              )}
            </div>
            <label className="field">Name on the card (leave blank to use the head's name)
              <input autoFocus value={cardForm.CardName ?? ''} placeholder="e.g. Shri & Smt. Haresh Abichandani & Family"
                onChange={(e) => setCardF('CardName', e.target.value)} />
            </label>
            <div className="mn-covers">
              Covers: {editing.members.map(m => m.GuestName).filter(Boolean).join(', ')}.
            </div>
            {/* The ONE guest count still worth keeping, and it is kept out of
                the card list: how many people come on the head's extra cards.
                It changes the Reception headcount, never a card count. */}
            {editing.extraCards > 0 && (
              <label className="field">
                People on the {editing.extraCards} extra cards (for the catering headcount)
                <input key={`p${editing.key}${editing.extraPersonsTyped}`} className="cards-num" type="number" min="0"
                  defaultValue={editing.extraPersonsTyped || ''} disabled={!canEdit}
                  placeholder={String(editing.extraPersons)}
                  onBlur={e => setExtraPersons(editing, e.target.value)} onKeyDown={commitOnEnter} />
              </label>
            )}
          </div>
        </Modal>
      )}

      {typeForm && (
        <Modal className="mn-type-modal" title={typeForm.CardTypeId ? 'Edit card type' : 'New card type'} onClose={closeType}
          footer={<>
            <span className="mn-sum"><strong>{typeForm.FunctionIds.length}</strong> {typeForm.FunctionIds.length === 1 ? 'function' : 'functions'}</span>
            <button className="btn light" onClick={closeType}>Cancel</button>
            <button className="btn primary" onClick={saveType}>Save</button>
          </>}>
          <div className="mn-line" onKeyDown={enterTo(saveType)}>
            <label className="field mn-grow">Name
              <input autoFocus value={typeForm.CardTypeName ?? ''} placeholder="e.g. Reception Card"
                onChange={(e) => setTypeF('CardTypeName', e.target.value)} />
            </label>
            <div className="mn-w150">
              <Field label="Kind" type="select" value={typeForm.Kind} options={CARD_KINDS} onChange={(v) => setTypeF('Kind', v)} />
            </div>
          </div>
          <div className="cg-fn-head" style={{ marginTop: 4 }}>
            <span className="step-label">For guests invited to any of</span>
          </div>
          <div className="cg-fn-grid" style={{ marginBottom: 10 }}>
            {functions.map(f => fnRow(f, typeForm.FunctionIds.includes(String(f.FunctionId)), () => toggleTypeFunction(f.FunctionId)))}
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!typeForm.ForExtraCards} onChange={e => setTypeF('ForExtraCards', e.target.checked ? 1 : 0)} />
            Extra cards are copies of this card
          </label>
          {!!typeForm.ForExtraCards && (
            <>
              <div className="cg-fn-head">
                <span className="step-label">Extra-card guests come to (counted in their headcount)</span>
              </div>
              <div className="cg-fn-grid">
                {functions.filter(f => typeForm.FunctionIds.includes(String(f.FunctionId)))
                  .map(f => fnRow(f, typeForm.ExtraFunctionIds.includes(String(f.FunctionId)), () => toggleTypeExtraFunction(f.FunctionId)))}
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
